import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { 
  Users, FileText, Settings, Menu, ChevronRight,
  Server, Activity, LayoutDashboard
} from 'lucide-react';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import GeneratePage from './pages/GeneratePage';
import SettingsPage from './pages/SettingsPage';

// Sidebar Navigation Component
function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation();
  
  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/customers', icon: Users, label: 'Customers' },
    { path: '/generate', icon: FileText, label: 'Generate Report' },
    { path: '/settings', icon: Settings, label: 'Settings' }
  ];
  
  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-slate-900 z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Server className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white tracking-tight">tekpro</span>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map(({ path, icon: Icon, label }) => {
            const isActive = location.pathname === path;
            return (
              <NavLink
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg font-medium
                  transition-all duration-150
                  ${isActive 
                    ? 'bg-primary text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }
                `}
                onClick={() => setIsOpen(false)}
              >
                <Icon className="w-5 h-5" />
                <span>{label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </NavLink>
            );
          })}
        </nav>
        
        {/* Status indicator */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800 pointer-events-none">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Activity className="w-4 h-4 text-green-500" />
            <span>System Online</span>
          </div>
        </div>
      </aside>
    </>
  );
}

// Header Component
function Header({ setIsOpen }) {
  const location = useLocation();
  
  const getPageTitle = () => {
    switch (location.pathname) {
      case '/': return 'Dashboard';
      case '/customers': return 'Customers';
      case '/generate': return 'Generate Report';
      case '/settings': return 'Settings';
      default: return 'Dashboard';
    }
  };
  
  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8">
      <button
        data-testid="mobile-menu-btn"
        className="lg:hidden p-2 rounded-lg hover:bg-slate-100 mr-4"
        onClick={() => setIsOpen(true)}
      >
        <Menu className="w-5 h-5" />
      </button>
      
      <h1 className="text-xl font-bold text-slate-900">{getPageTitle()}</h1>
      
      <div className="ml-auto flex items-center gap-4">
        <span className="text-sm text-slate-500">Tekkonnectpro IT Services</span>
      </div>
    </header>
  );
}

// Main App Component
function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <Router>
      <div className="min-h-screen bg-background flex">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        
        <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
          <Header setIsOpen={setSidebarOpen} />
          
          <main className="flex-1 p-4 lg:p-8 overflow-auto">
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/generate" element={<GeneratePage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;
